import { createFileRoute, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { CtLogo } from '@/components/brand/index.js';
import { attachmentNote } from '@/features/automations/attachment-note.js';
import { SharedReportViewer } from '@/features/insights/SharedReportViewer.js';
import {
  fetchSharedAutomation,
  type SharedAutomation,
} from '@/lib/api/automation-shares.js';
import type { AutomationResult } from '@/lib/api/automations.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';
import { signInWithSso } from '@/lib/sso.js';
import { logger } from '@/logger.js';
import { MessageBody } from '@/ui/chat/atoms/MessageBody.js';
import { PageContainer } from '@/ui/PageContainer.js';

/**
 * No `beforeLoad` guard here: `boot.tsx` picks the gate-free public tree once from the first pathname,
 * so a redirect to `/` from this route would land inside the app with no `AuthGate`. A switched-off
 * Scheduled section is handled in place instead (see `SharedAutomationPage`).
 */
export const Route = createFileRoute('/scheduled_/shared/$shareId')({
  component: SharedAutomationPage,
});

type ViewerState =
  | { kind: 'loading' }
  | { kind: 'ready'; automation: SharedAutomation }
  | { kind: 'needs-auth' }
  | { kind: 'unavailable' }
  | { kind: 'error'; message: string };

/**
 * Chrome-free public viewer for a shared automation's results.
 *
 * Deliberately isolated from the authenticated app, same reasoning as `dashboards_.shared.$shareId.tsx`:
 * no Teams SDK, no identity bootstrap. The `/scheduled/shared/*` bypass in `boot.tsx` (via
 * `isPublicViewPath`) is what lets this render with no session at all. A link carries the results and
 * nothing else — there is no control here to run, pause or change the automation, because a reader of a
 * link is not a member of the workspace that owns it.
 */
function SharedAutomationPage(): JSX.Element {
  const { shareId } = useParams({ from: '/scheduled_/shared/$shareId' });
  const [state, setState] = useState<ViewerState>({ kind: 'loading' });

  /**
   * The share this page is currently showing. The router reuses the component when only `$shareId`
   * changes, so a read for the previous link can settle after the new one and paint one share's
   * results — a named link's among them — under the other's URL.
   */
  const shownFor = useRef(shareId);
  shownFor.current = shareId;

  const load = useCallback(async (): Promise<void> => {
    /** An admin who turned the section off closed its links too; nothing is fetched for one. */
    if (!getRuntimeConfig().features.automations) {
      setState({ kind: 'unavailable' });
      return;
    }
    const requestedFor = shareId;
    setState({ kind: 'loading' });
    try {
      const result = await fetchSharedAutomation(requestedFor);
      if (shownFor.current !== requestedFor) return;
      if (result.kind === 'ready') {
        setState({ kind: 'ready', automation: result.automation });
      } else {
        setState(result);
      }
    } catch (reason: unknown) {
      if (shownFor.current !== requestedFor) return;
      logger.warn('Shared automation fetch failed', reason);
      setState({
        kind: 'error',
        message: 'Something went wrong loading these results.',
      });
    }
  }, [shareId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === 'loading') {
    return (
      <ViewerShell>
        <p className="text-sm text-muted-foreground" role="status">
          Loading results…
        </p>
      </ViewerShell>
    );
  }

  if (state.kind === 'needs-auth') {
    return <SignInScreen onSignedIn={() => void load()} />;
  }

  if (state.kind === 'unavailable') {
    return (
      <ViewerShell>
        <p className="text-sm text-muted-foreground">
          These results are unavailable or the link has been revoked.
        </p>
      </ViewerShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <ViewerShell>
        <p className="text-sm text-destructive">{state.message}</p>
      </ViewerShell>
    );
  }

  return <ReadOnlyResults shareId={shareId} automation={state.automation} />;
}

/**
 * Renders the ready state: the automation's name, when it last ran, and its results in the order the
 * service serves them (newest first), in the same 4xl page container and card the app's own pages use,
 * so a link shows the results at the width their author saw.
 */
function ReadOnlyResults({
  shareId,
  automation,
}: {
  shareId: string;
  automation: SharedAutomation;
}): JSX.Element {
  const lastRunLabel =
    automation.lastRunAt === null
      ? 'No runs yet'
      : `Last run ${new Date(automation.lastRunAt).toLocaleString()}`;

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col"
      style={{ height: 'var(--app-height, 100dvh)' }}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border/40 bg-background px-4 py-3">
        <CtLogo
          variant="mark"
          tone="mono"
          className="size-6 text-[#18399b] [[data-theme='dark']_&]:text-white"
          aria-label="Customertimes"
        />
        <span className="text-sm font-medium text-foreground">
          Read-only automation results
        </span>
      </header>
      <div className="min-h-0 flex-1 overflow-auto">
        <PageContainer maxWidth="5xl">
          <article className="mx-auto flex w-full max-w-[864px] flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-elev-1">
            <header className="flex min-w-0 flex-col gap-0.5">
              <h1 className="truncate text-sm font-semibold text-foreground">
                {automation.name}
              </h1>
              <p className="text-caption text-muted-foreground">
                {lastRunLabel}
              </p>
            </header>
            {automation.results.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No results to show yet.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-border/40">
                {automation.results.map((result) => (
                  <li key={result.id} className="flex flex-col gap-1 py-3">
                    <ResultEntry shareId={shareId} result={result} />
                  </li>
                ))}
              </ul>
            )}
          </article>
        </PageContainer>
      </div>
    </div>
  );
}

/**
 * One run's output: when it ran, what it said, and — where the run produced files — how many were left
 * out. The note comes from the same {@link attachmentNote} the in-app run history uses, so one run reads
 * the same on the link as it does to a colleague inside the workspace. A result with a report renders it
 * inline through the share's own anonymous proxy, sized the same as the authenticated feed's viewer.
 */
function ResultEntry({
  shareId,
  result,
}: {
  shareId: string;
  result: AutomationResult;
}): JSX.Element {
  const note = attachmentNote(result.attachmentCount);

  return (
    <>
      <time
        dateTime={result.createdAt}
        className="text-caption text-muted-foreground"
      >
        {new Date(result.createdAt).toLocaleString()}
      </time>
      <MessageBody body={result.text} sanitize />
      {note !== null && (
        <span className="text-caption text-muted-foreground">{note}</span>
      )}
      {result.reportRunId !== null && (
        <div className="mx-auto h-[70vh] w-full max-w-[832px] overflow-hidden rounded-lg border border-border">
          <SharedReportViewer shareId={shareId} runId={result.reportRunId} />
        </div>
      )}
    </>
  );
}

/** Shared full-screen frame for the loading / unavailable / error states. */
function ViewerShell({ children }: { children: JSX.Element }): JSX.Element {
  return (
    <div
      className="flex w-full flex-col items-center justify-center gap-6 bg-background px-6 text-center"
      style={{ height: 'var(--app-height, 100dvh)' }}
    >
      <div className="flex items-center gap-3">
        <CtLogo
          variant="mark"
          tone="mono"
          className="size-9 text-[#18399b] [[data-theme='dark']_&]:text-white"
          aria-label="Customertimes"
        />
        <span className="font-heading text-xl font-bold tracking-tight text-foreground">
          Agent
        </span>
      </div>
      {children}
    </div>
  );
}

/** 401 screen — these results are shared by name, so a Customertimes sign-in is required. */
function SignInScreen({ onSignedIn }: { onSignedIn: () => void }): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSignIn(): Promise<void> {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      await signInWithSso();
      onSignedIn();
    } catch {
      setError('Sign-in failed. Try again.');
      setLoading(false);
    }
  }

  return (
    <ViewerShell>
      <div className="flex flex-col items-center gap-4">
        <p className="max-w-sm text-sm text-muted-foreground">
          These results are shared with specific Customertimes employees.
        </p>
        <button
          type="button"
          onClick={() => void onSignIn()}
          disabled={loading}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground transition-all hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Signing in…' : 'Sign in with Microsoft to view'}
        </button>
        {error !== null && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </ViewerShell>
  );
}
